/**
 * @file legal-card-actions.ts
 * @description Какие кнопки сохранения показать в карточке документа и что
 * мешает публикации (phase19.16, SB139, SB141, SB143; SC34 — одно главное
 * действие, SC52 — без переключателя «Опубликовать»).
 *
 * Чистые функции — правила проверяются тестом без разметки:
 *   - группа: опубликована — главное «Сохранить» (правка сразу на сайте,
 *     редакций нет); нет — «Сохранить и опубликовать» и «Сохранить»;
 *   - текст и PDF: снят с публикации без правок — «Показать снова» (та же
 *     редакция, решение §7.27); иначе «Сохранить и опубликовать» и
 *     «Сохранить черновик».
 * Причина «нельзя опубликовать» — первое из `legalPublishIssues` (shared;
 * те же правила, по которым API ответит 422/409).
 */

import { LEGAL_ERROR_MESSAGES, legalPublishIssues, type LegalDocumentDto } from "@chaiberi/shared";

import type { LegalDraftValues } from "../../hooks/legal/useLegalDraftForm";

/** Подсказка у неактивной публикации, когда изменений нет. */
export const LEGAL_NOTHING_TO_PUBLISH_TEXT = "Изменений для публикации нет.";

export interface LegalCardButtons {
  /** Главная кнопка: подпись и действие. */
  main: { label: string; run: "publish" | "save" };
  /** Вторая кнопка сохранения (без публикации); null — не нужна. */
  save: string | null;
}

type ButtonsDoc = Pick<LegalDocumentDto, "kind" | "isPublished" | "status" | "hasDraftChanges">;

export function legalCardButtons(doc: ButtonsDoc, dirty: boolean): LegalCardButtons {
  if (doc.kind === "GROUP") {
    return doc.isPublished
      ? { main: { label: "Сохранить", run: "save" }, save: null }
      : { main: { label: "Сохранить и опубликовать", run: "publish" }, save: "Сохранить" };
  }
  if (doc.status === "unpublished" && !dirty && !doc.hasDraftChanges) {
    return { main: { label: "Показать снова", run: "publish" }, save: "Сохранить черновик" };
  }
  return { main: { label: "Сохранить и опубликовать", run: "publish" }, save: "Сохранить черновик" };
}

type IssuesDoc = Pick<LegalDocumentDto, "kind" | "isPublished" | "live" | "hasDraftChanges" | "file" | "members" | "groupSource">;

/** Сколько документов группы видно на сайте; у автоматической группы состав считает сервер. */
function visibleMembersOf(doc: IssuesDoc): number {
  if (doc.groupSource === "PRODUCER_CERTIFICATES") return 1;
  return (doc.members ?? []).filter((m) => m.visibleOnSite).length;
}

/** Почему публикация не пройдёт (текст) или null. Новый документ — `doc` null. */
export function legalPublishBlock(doc: IssuesDoc | null, kind: LegalDocumentDto["kind"], values: LegalDraftValues, dirty: boolean, hasPendingFile = false): string | null {
  const issues = legalPublishIssues({
    kind,
    isPublished: doc?.isPublished ?? false,
    liveRevisionId: doc?.live ? "live" : null,
    hasDraftChanges: (doc?.hasDraftChanges ?? false) || dirty,
    visibleMembers: doc ? visibleMembersOf(doc) : 0,
    description: values.description,
    contentHtml: values.contentHtml,
    hasFile: (doc?.file ?? null) !== null || hasPendingFile,
  });
  const first = issues[0];
  if (!first) return null;
  // Опубликован и правок нет — короткая подсказка вместо ответа API «правок нет…» (QA: иначе 409 в консоли).
  return first === "legal_nothing_to_publish" ? LEGAL_NOTHING_TO_PUBLISH_TEXT : LEGAL_ERROR_MESSAGES[first];
}
